/**
 * Navigation Service
 *
 * Centralized navigation service to handle all routing operations.
 * This abstraction layer makes it easier to:
 * 1. Migrate from react-router-redux to direct history usage
 * 2. Upgrade to newer router versions in the future
 * 3. Maintain consistent navigation behavior across the app
 * 4. Add navigation middleware (analytics, logging, etc.)
 */

import { browserHistory } from "store/createStore";

class NavigationService {
  constructor() {
    this.history = browserHistory;
  }

  /**
   * Navigate to a new location and add it to the history stack
   * @param {string|object} location - Path string or location object
   * @param {object} state - Optional state to pass with navigation
   */
  push(location, state = {}) {
    if (typeof location === "string") {
      this.history.push({
        pathname: location,
        state,
      });
    } else {
      this.history.push({
        ...location,
        state: { ...location.state, ...state },
      });
    }
  }

  /**
   * Replace the current location in the history stack
   * @param {string|object} location - Path string or location object
   * @param {object} state - Optional state to pass with navigation
   */
  replace(location, state = {}) {
    if (typeof location === "string") {
      this.history.replace({
        pathname: location,
        state,
      });
    } else {
      this.history.replace({
        ...location,
        state: { ...location.state, ...state },
      });
    }
  }

  /**
   * Go back one step in the history stack
   */
  goBack() {
    this.history.goBack();
  }

  /**
   * Go forward one step in the history stack
   */
  goForward() {
    this.history.goForward();
  }

  /**
   * Go to a specific point in the history stack
   * @param {number} n - Number of steps (negative for back, positive for forward)
   */
  go(n) {
    this.history.go(n);
  }

  /**
   * Get the current location
   * @returns {object} Current location object
   */
  getCurrentLocation() {
    return this.history.getCurrentLocation();
  }

  /**
   * Listen for location changes
   * @param {function} listener - Callback function to handle location changes
   * @returns {function} Unsubscribe function
   */
  listen(listener) {
    return this.history.listen(listener);
  }

  /**
   * Create an href for a location
   * @param {string|object} location - Path string or location object
   * @returns {string} href string
   */
  createHref(location) {
    return this.history.createHref(location);
  }

  /**
   * Block navigation with a prompt message
   * @param {string|function} message - Block message or function returning message
   * @returns {function} Unblock function
   */
  block(message) {
    return this.history.block(message);
  }

  /**
   * Get the current pathname
   * @returns {string} Current pathname
   */
  getCurrentPathname() {
    return this.getCurrentLocation().pathname;
  }

  /**
   * Get the current search params
   * @returns {string} Current search string
   */
  getCurrentSearch() {
    return this.getCurrentLocation().search;
  }

  /**
   * Get the current query parameters as an object
   * @returns {object} Query parameters object
   */
  getCurrentQuery() {
    const searchParams = new URLSearchParams(this.getCurrentSearch());
    const query = {};
    for (const [key, value] of searchParams.entries()) {
      query[key] = value;
    }
    return query;
  }

  /**
   * Check if we can go back
   * @returns {boolean} True if can go back
   */
  canGoBack() {
    // Note: This is a simple implementation
    // For more accurate tracking, you'd need to maintain your own history state
    return window.history.length > 1;
  }

  /**
   * Navigate to external URL
   * @param {string} url - External URL
   * @param {boolean} newTab - Open in new tab (default: false)
   */
  navigateExternal(url, newTab = false) {
    if (newTab) {
      // Use the existing openLinkInNewTab utility to comply with ESLint rules
      // This should be imported and used from the thunk that calls this method
      console.warn("Use openLinkInNewTab thunk for opening URLs in new tabs");
    } else {
      window.location.href = url;
    }
  }

  /**
   * Reload the current page
   */
  reload() {
    window.location.reload();
  }

  /**
   * Set the history instance (useful for testing or router upgrades)
   * @param {object} history - History instance
   */
  setHistory(history) {
    this.history = history;
  }

  /**
   * Get the history instance
   * @returns {object} Current history instance
   */
  getHistory() {
    return this.history;
  }
}

// Create and export a singleton instance
const navigationService = new NavigationService();

// Export both the class and the singleton for flexibility
export { NavigationService };
export default navigationService;

// Export individual methods for direct import (backward compatibility)
// Use arrow functions to maintain proper 'this' binding
export const push = (...args) => navigationService.push(...args);
export const replace = (...args) => navigationService.replace(...args);
export const goBack = () => navigationService.goBack();
export const goForward = () => navigationService.goForward();
export const go = (...args) => navigationService.go(...args);
export const getCurrentLocation = () => navigationService.getCurrentLocation();
export const listen = (...args) => navigationService.listen(...args);
export const createHref = (...args) => navigationService.createHref(...args);
export const block = (...args) => navigationService.block(...args);
export const getCurrentPathname = () => navigationService.getCurrentPathname();
export const getCurrentSearch = () => navigationService.getCurrentSearch();
export const getCurrentQuery = () => navigationService.getCurrentQuery();
export const canGoBack = () => navigationService.canGoBack();
export const navigateExternal = (...args) =>
  navigationService.navigateExternal(...args);
export const reload = () => navigationService.reload();
